import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Build as GenericIcon,
  Engineering as BodyworkIcon,
  Brush as CleaningIcon,
  Palette as UpholsteryIcon,
  LocalGasStation as FuelIcon,
  Security as InsuranceIcon,
  Settings as AccessoriesIcon,
  Receipt as TaxIcon,
  QuestionMark as OtherIcon,
  Construction as MaintenanceIcon,
  DateRange as DateIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { Expense, ExpenseCategory, EXPENSE_CATEGORIES, Vehicle } from '../../types';
// @ts-ignore
import { format } from 'date-fns';
import vehicleService from '../../services/vehicleService';
import { pdf } from '@react-pdf/renderer';
import VehicleExpensesPDF from './VehicleExpensesPDF';
import { exportVehicleExpensesReportExcel } from '../../utils/reportExcelExports';

// Componente para exibir o ícone da categoria de despesa
const ExpenseCategoryIcon = ({ category }: { category: ExpenseCategory }) => {
  switch (category) {
    case 'maintenance':
      return <MaintenanceIcon />;
    case 'mechanics':
      return <GenericIcon />;
    case 'bodywork':
      return <BodyworkIcon />;
    case 'upholstery':
      return <UpholsteryIcon />;
    case 'fuel':
      return <FuelIcon />;
    case 'insurance':
      return <InsuranceIcon />;
    case 'tax':
      return <TaxIcon />;
    case 'accessories':
      return <AccessoriesIcon />;
    case 'cleaning':
      return <CleaningIcon />;
    case 'other':
      return <OtherIcon />;
    default:
      return <GenericIcon />;
  }
};

const getCategoryColor = (category: ExpenseCategory): string => {
  switch (category) {
    case 'maintenance': return '#4caf50';
    case 'mechanics': return '#2196f3';
    case 'bodywork': return '#ff9800';
    case 'upholstery': return '#9c27b0';
    case 'fuel': return '#f44336';
    case 'insurance': return '#607d8b';
    case 'tax': return '#795548';
    case 'accessories': return '#00bcd4';
    case 'cleaning': return '#8bc34a';
    case 'other': return '#9e9e9e';
    default: return '#9e9e9e';
  }
};

const VehicleExpenses: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalExpenses, setTotalExpenses] = useState<number>(0)
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);


  useEffect(() => {
    const fetchVehicleDetails = async () => {
      try {
        setLoading(true);
        // Uso do serviço real em vez de dados mockados
        const data = await vehicleService.getVehicle(Number(id));

        if(data.expenses){
          setExpenses(data.expenses);
          setTotalExpenses(data.expenses.reduce((sum, expense) => sum + expense.amount, 0));
        }
        setVehicle(data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar detalhes do veículo:', error);
        setError('Não foi possível carregar os detalhes do veículo. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchVehicleDetails();
  }, [id]);


  const handleAddExpense = () => {
    navigate(`/expenses/new?vehicle_id=${id}`);
  };

  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const getFullImageUrl = (url?: string) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!API_BASE) return url;
    return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const getVehiclePrimaryImage = (v: Vehicle): string | null => {
    const first = (v.images || [])[0]?.url;
    return first ? getFullImageUrl(first) : null;
  };

  const handleDownloadPdf = async () => {
    if (!vehicle) return;
    setDownloadingPdf(true);
    try {
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;
      const doc = (
        <VehicleExpensesPDF
          vehicle={vehicle}
          expenses={expenses || []}
          generatedAtLabel={generatedAtLabel}
          vehicleImageUrl={getVehiclePrimaryImage(vehicle)}
          logoUrl={logoUrl}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Despesas-${vehicle.brand}-${vehicle.model}-${String(vehicle.year)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Erro ao gerar PDF das despesas.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!vehicle) return;
    setDownloadingExcel(true);
    try {
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      await exportVehicleExpensesReportExcel({
        vehicle,
        expenses: expenses || [],
        generatedAtLabel,
      });
    } catch (e) {
      console.error(e);
      setError('Erro ao gerar Excel das despesas.');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleViewExpense = (expenseId: number) => {
    navigate(`/despesas/${expenseId}`);
  };

  const handleBackToVehicles = () => {
    navigate('/veiculos');
  };

  // Formatar valores monetários
  const formatCurrency = (value: number) => {
    
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'; // Retorna um hífen se a data não existir
    
    try {
      const cleanDateString = dateString.split('T')[0];  // Vai pegar apenas '2025-05-01'
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-'; // Retorna um hífen em caso de erro
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToVehicles}>
          Voltar para lista de veículos
        </Button>
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
          Veículo não encontrado.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToVehicles}>
          Voltar para lista de veículos
        </Button>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackToVehicles}
            sx={{ mr: 2 }}
            color="inherit"
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Despesas do Veículo
          </Typography>
          <Button
            variant="outlined"
            color="success"
            startIcon={<ExcelIcon />}
            onClick={() => void handleDownloadExcel()}
            disabled={downloadingExcel}
            sx={{ borderRadius: '8px', mr: 1 }}
          >
            {downloadingExcel ? 'Gerando Excel...' : 'Baixar Excel'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PdfIcon />}
            onClick={() => void handleDownloadPdf()}
            disabled={downloadingPdf}
            sx={{ borderRadius: '8px', mr: 1 }}
          >
            {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddExpense}
            sx={{ borderRadius: '8px' }}
          >
            Nova Despesa
          </Button>
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mb: 4,
          }}
        >
          {/* Informações do veículo */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 56,
                height: 56,
                mr: 2,
              }}
            >
              <CarIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                {vehicle.brand} {vehicle.model} ({vehicle.year})
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Placa: {vehicle.plate} | Cor: {vehicle.color}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Resumo financeiro */}
          <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <MoneyIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Resumo Financeiro
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: '12px', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Preço de Compra
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {vehicle.is_consignado ? 'N/A' : vehicle.purchase_price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: '12px', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total de Despesas
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                    {formatCurrency(totalExpenses)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ borderRadius: '12px', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Custo Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(vehicle.purchase_price + totalExpenses)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Lista de despesas */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h3" sx={{ display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
              Histórico de Despesas
            </Typography>
            {expenses.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Total de {expenses.length} {expenses.length === 1 ? 'despesa' : 'despesas'}
              </Typography>
            )}
          </Box>

          {expenses.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: '8px',
              }}
            >
              <MoneyIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6">Nenhuma despesa registrada</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Este veículo ainda não possui despesas registradas.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddExpense}
                sx={{ borderRadius: '8px' }}
              >
                Registrar Despesa
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="center">Data</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id} hover onClick={() => handleViewExpense(expense.id)}>
                      <TableCell>
                        <Chip
                          icon={<ExpenseCategoryIcon category={expense.category} />}
                          label={EXPENSE_CATEGORIES[expense.category]}
                          size="small"
                          sx={{
                            bgcolor: getCategoryColor(expense.category) + '20',
                            color: getCategoryColor(expense.category),
                            fontWeight: 'medium',
                          }}
                        />
                      </TableCell>
                      <TableCell>{expense.description}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DateIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          {formatDate(expense.expense_date)}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewExpense(expense.id);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                      {formatCurrency(totalExpenses)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </div>
    </Fade>
  );
};

export default VehicleExpenses; 